import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, ChevronDown, ChevronRight, Smartphone, Chrome } from 'lucide-react';
import { useToolTheme } from '@/contexts/ThemeContext';
import { isMobile, isAndroid, isSamsung } from 'react-device-detect';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare global {
  interface WindowEventMap {
    'beforeinstallprompt': BeforeInstallPromptEvent;
  }
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [show, setShow] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const { toolColors } = useToolTheme();

  useEffect(() => {
    // Vérifier si l'application est déjà installée
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches;
    
    if (isInstalled) {
      return;
    }

    // Vérifier si l'utilisateur a déjà refusé l'installation
    const hasDeclined = localStorage.getItem('pwaInstallDeclined');
    if (hasDeclined) {
      return;
    }

    const handler = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };

    window.addEventListener('beforeinstallprompt', handler as any);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler as any);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setShow(false);
      } else {
        localStorage.setItem('pwaInstallDeclined', 'true');
      }
    } catch (error) {
      console.error('Error installing app:', error);
    }
  };

  const handleClose = () => {
    localStorage.setItem('pwaInstallDeclined', 'true');
    setShow(false);
  };

  const getInstallInstructions = () => {
    if (isSamsung) {
      return (
        <div className="space-y-3 text-sm">
          <p className="font-medium">Pour installer l'application sur votre Samsung :</p>
          <ol className="space-y-2 list-decimal pl-4">
            <li>Ouvrez Chrome sur votre téléphone</li>
            <li>Appuyez sur les trois points (⋮) en haut à droite</li>
            <li>Sélectionnez "Installer l'application" ou "Ajouter à l'écran d'accueil"</li>
            <li>Suivez les instructions à l'écran</li>
          </ol>
          <div className="flex items-center gap-2 mt-4 p-2 bg-blue-50 rounded-lg">
            <Chrome className="w-5 h-5 text-blue-600" />
            <span className="text-blue-700">Conseil : Utilisez Chrome pour une meilleure expérience</span>
          </div>
        </div>
      );
    }

    if (isAndroid) {
      return (
        <div className="space-y-3 text-sm">
          <p className="font-medium">Pour installer l'application sur Android :</p>
          <ol className="space-y-2 list-decimal pl-4">
            <li>Appuyez sur le bouton "Installer l'application"</li>
            <li>Dans la fenêtre qui s'affiche, cliquez sur "Installer"</li>
            <li>L'application sera ajoutée à votre écran d'accueil</li>
          </ol>
        </div>
      );
    }

    return (
      <div className="space-y-3 text-sm">
        <p className="font-medium">Pour installer l'application :</p>
        <ol className="space-y-2 list-decimal pl-4">
          <li>Cliquez sur le bouton "Installer l'application"</li>
          <li>Suivez les instructions de votre navigateur</li>
        </ol>
      </div>
    );
  };

  if (!isMobile || !show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="fixed bottom-4 left-4 right-4 z-50"
      >
        <Card className="shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Smartphone className="w-6 h-6" style={{ color: toolColors.primary }} />
                </div>
                <div>
                  <h3 className="font-semibold">Installer l'application</h3>
                  <p className="text-sm text-gray-600">
                    Accédez rapidement à votre baromètre énergétique
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="mt-4 space-y-3">
              <Button
                variant="outline"
                onClick={() => setShowInstructions(!showInstructions)}
                className="w-full justify-between"
              >
                Comment installer l'application ?
                {showInstructions ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </Button>
              
              <AnimatePresence>
                {showInstructions && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden mt-3 p-3 bg-gray-50 rounded-lg"
                  >
                    {getInstallInstructions()}
                  </motion.div>
                )}
              </AnimatePresence>

              <Button 
                onClick={handleInstall}
                className="w-full"
                style={{ 
                  backgroundColor: toolColors.primary,
                  color: 'white'
                }}
              >
                <Download className="w-4 h-4 mr-2" />
                Installer maintenant
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
}